import type { ByPart } from './partModelSize';
import type { PartProfile } from './partProfile';
import type { PartItem } from './partsRun';
import { hardwareReachOf, holePlace, holeStyleOf, holeWidthOf, type HoleStyle, type StepHole } from './stepHoles';

// ONE BORE, RESOLVED AGAINST A MESH AND READY TO SEND. What Find holes answered is a place said in
// words — "35 mm from the Back edge", "centred" — and what the drill takes is two millimetre
// figures from a face's own corner. Worked out here once, so the part view's Drill buttons and
// the Holes & covers run's batch cut identically (see HolesTable, drillParts).
export type Bore = {
  hole: StepHole; u: number; v: number; diameter: number; depth: number | null;
  style: HoleStyle;
};

// HOW DEEP A BORE GOES, AND WHETHER ANYBODY SAID SO. Find holes is allowed to answer null, and
// on a real manual it mostly does: a drawing that dimensions a hole's position rarely dimensions
// its depth. The FASTENER answers where the drawing does not — a 40 mm dowel needs 40 mm of hole —
// which is weaker than a stated depth and is the one number in the room that is about this bore.
// Never written back into the answer.
export function holeDepthOf(h: StepHole, models?: ByPart): { mm: number | null; said: boolean } {
  if (h.through) return { mm: null, said: true };
  if (h.depthMm != null) return { mm: h.depthMm, said: true };
  // As far as the fastener goes in: its reach, head left out (see hardwareReachOf).
  return { mm: h.via != null ? hardwareReachOf(String(h.via), models) : null, said: false };
}

// A bore needs three things the drill will not do without: a place the part's extents can
// resolve, a width, and a depth (or that it goes through) — and its shape, read out of the
// ontology, which is null until that has loaded (a dowel hole bored meanwhile would get a point).
// The bore, or which of those it lacks; null while the shape is not known yet. And 'wood' first:
// a place its line found off the part's wood (StepHole.offWood), or on a line that does not work
// (StepHole.unfit), is not one to cut at all.
export function boreOf(
  hole: StepHole,
  dims: { x: number; y: number; z: number } | null,
  profile: PartProfile | null,
  items: PartItem[],
  models: ByPart,
): Bore | 'wood' | 'place' | 'width' | 'depth' | null {
  if (hole.offWood || hole.unfit) return 'wood';
  const at = holePlace(hole, dims, profile);
  if (!at) return 'place';
  const diameter = holeWidthOf(hole, items, models);
  if (!diameter) return 'width';
  const depth = holeDepthOf(hole, models);
  if (!hole.through && !depth.mm) return 'depth';
  const style = holeStyleOf(hole, items);
  if (!style) return null;
  return {
    hole, style, diameter, depth: depth.mm,
    u: Math.round(at.u * 10) / 10,
    v: Math.round(at.v * 10) / 10,
  };
}

// Two bores at the same spot, of the same width — one cut.
export const sameBore = (a: { face: string; u: number; v: number; diameter: number }, b: typeof a) =>
  a.face === b.face && Math.abs(a.u - b.u) < 0.5 && Math.abs(a.v - b.v) < 0.5 && Math.abs(a.diameter - b.diameter) < 0.1;

// WHAT THE DRILL IS SENT FOR ONE BORE — and its counterbore where the head is sunk: as wide as
// the head the hardware prints, or twice the bore where it prints none, a screw head's usual
// proportion.
export function boreRequest(b: Bore, items: PartItem[]) {
  return {
    // Its id, kept on the history's entry (see PartHistories).
    id: b.hole.id,
    face: b.hole.face,
    u: b.u,
    v: b.v,
    diameter: b.diameter,
    depth: b.hole.through ? null : b.depth,
    through: b.hole.through,
    style: b.style,
    ...(b.hole.sunkMm ? {
      sunk: b.hole.sunkMm,
      head: (b.hole.via != null ? items[Number(b.hole.via)]?.size?.headDiaMm : null) ?? b.diameter * 2,
    } : {}),
  };
}
